/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2021, 2024
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/

import _ from 'lodash';
import moment from 'moment-timezone';
import { createSelector } from 'reselect';

import {
  areActionsLoading,
  selectUserCreatedTopics
} from '@ibm-watson/shared/modules/actions/store/actions/selectors';
import { getAll } from '@ibm-watson/shared/modules/actions/store/intents/selectors';
import messages from '@ibm-watson/shared/modules/analytics/messages';
import {
  selectIsDialogEnabledInDraftEnvironment,
  selectTargetEnvironmentId
} from '@ibm-watson/shared/modules/ia/store/environments/selectors';
import {
  hasPublishedVersion,
  selectIsPublishStatusLoading
} from '@ibm-watson/shared/modules/ia/store/publish/selectors';
import { ApiStatus } from '../../../ts';
import {
  ActionSkillTypes,
  AnalyticsActionSkillTypes
} from '../../../ts/action';
import type { AnalyticsFilters } from '../../../ts/analytics';
import { FeatureFlag } from '../../../ts/feature-flags';
import { PlanName } from '../../../ts/plan';
import type { RootState } from '../../../ts/state';
import {
  isFeatureEnabled,
  selectIsConversationalSearchEnabled
} from '../../ia/store/features/selectors';
import { isCpd } from '../../ia/util/config';
import {
  isValidNumber,
  unrecognizedPollingStatus
} from '../util/analyticsUtil';

export const selectEnvironmentIdFromFilters = (state: RootState): string =>
  state.analytics?.filters.environment;

export const selectFilters = (
  state: RootState
): AnalyticsFilters & { assistantId?: string } => {
  return {
    environment: selectEnvironmentIdFromFilters(state),
    assistantId: state.session?.assistantId,
    startDate: state.analytics?.filters.startDate,
    endDate: state.analytics?.filters.endDate,
    interval: state.analytics?.filters.interval,
    range: state.analytics?.filters.range,
    containment: state.analytics?.filters.containment,
    recognition: state.analytics?.filters.recognition,
    search: state.analytics?.filters.search,
    topics: state.analytics?.filters.topics,
    entities: state.analytics?.filters?.entities,
    resolution_method: state.analytics?.filters?.resolution_method,
    keywords: state.analytics?.filters.keywords,
    noIntents: state.analytics?.filters.noIntents,
    sortOrder: state.analytics?.filters.sortOrder,
    incompleteReasons: state.analytics?.filters.incompleteReasons,
    completeReasons: state.analytics?.filters.completeReasons,
    percentageReasons: state.analytics?.filters.percentageReasons
  };
};

export const numberOfDaysSelected = (state: RootState): number => {
  const first = moment(state.analytics?.filters.startDate);
  const second = moment(state.analytics?.filters.endDate);
  const diff = second.diff(first, 'days');
  return moment.duration(diff, 'days').asDays();
};

export const selectReportsLoading = (state: RootState): boolean => {
  const {
    messages,
    conversations,
    activeUsers,
    mostPopular,
    leastPopular,
    intentContainment,
    coverage,
    containment
  } = state.analytics;

  return [
    messages.loading,
    conversations.loading,
    activeUsers.loading,
    mostPopular.loading,
    leastPopular.loading,
    intentContainment.loading,
    coverage.loading,
    containment.loading
  ].includes(ApiStatus.LOADING);
};

export const selectActiveUsersLoading = (state: RootState): boolean => {
  const { loading } = state.analytics?.activeUsers;
  return (
    loading?.includes(ApiStatus.IDLE) || loading?.includes(ApiStatus.LOADING)
  );
};

export const selectConversationsLoading = (state: RootState): boolean => {
  const { loading } = state.analytics?.conversations;
  return (
    loading?.includes(ApiStatus.IDLE) || loading?.includes(ApiStatus.LOADING)
  );
};

export const selectSingleConversationLoading = (state: RootState): boolean => {
  const { loading } = state.analytics?.singleConversation;
  return (
    loading?.includes(ApiStatus.IDLE) || loading?.includes(ApiStatus.LOADING)
  );
};

export const selectGenerativeAiLoading = (state: RootState): boolean => {
  const { loading } = state.analytics?.generativeAiMetrics;
  return (
    loading?.includes(ApiStatus.IDLE) || loading?.includes(ApiStatus.LOADING)
  );
};

export const selectUserRequestsLoading = (state: RootState): boolean => {
  const { loading } = state.analytics?.userRequests;
  return (
    loading?.includes(ApiStatus.IDLE) || loading?.includes(ApiStatus.LOADING)
  );
};

export const selectLogsLoading = (state: RootState): boolean => {
  return state.analytics?.messageLogs?.loading.includes(ApiStatus.LOADING);
};

export const selectConvoLogsLoading = (state: RootState): boolean =>
  state.analytics?.convoSearchMessageLogs?.loading?.includes(ApiStatus.LOADING);

export const selectIntentActionPairs = (state: RootState) => {
  return state.analytics?.intentActionPairs?.intentActionPairs;
};

const getTopicCategory = (category: string) => {
  switch (category) {
    case ActionSkillTypes.STANDARD:
      return messages.customBuiltActions.defaultMessage;
    case ActionSkillTypes.AI_GUIDED:
      return messages.aiGuidedActions.defaultMessage;
    case ActionSkillTypes.CONVERSATIONAL_SKILL:
      return messages.skillBasedActions.defaultMessage;
    default:
      return 'Actions';
  }
};

export const selectAllTopics = createSelector(
  [
    selectIsDialogEnabledInDraftEnvironment,
    selectUserCreatedTopics,
    getAll,
    selectIntentActionPairs
  ],
  (isDialogEnabled: boolean, topics, intents, intentActionPairs) => {
    let allTopics = [];
    //check if there are actions
    if (!_.isEmpty(topics)) {
      const actionTopics: { id: string; text: string; category: string }[] = [];
      _.forEach(topics, topic => {
        actionTopics.push({
          id: (topic as any).id,
          text: (topic as any).title || 'Untitled action',
          category: getTopicCategory((topic as any).type)
        });
      });

      allTopics = allTopics.concat(_.sortBy(actionTopics, 'text'));
    }

    if (isDialogEnabled && !_.isEmpty(intents)) {
      const dialogIntents = [];

      _.forEach(intents, (value, index) => {
        dialogIntents.push({
          id: `option-intent-${index}`,
          text: '#' + value?.intent,
          category: 'Intents'
        });
      });

      if (!_.isEmpty(intentActionPairs)) {
        _.forEach(intentActionPairs, value => {
          const split = value.split(/[()]+/).filter(function (e) {
            return e;
          });
          const foundAction = topics?.find(
            item => (item as any).id === split[1]
          );
          const actionName = foundAction
            ? (foundAction as any).title
            : 'Untitled action';
          const intentName = '#' + split[0];
          dialogIntents.push({
            id: value,
            text: intentName + '  >  ' + actionName,
            intent: intentName,
            action: actionName,
            category: 'Transferred'
          });
        });
      }

      dialogIntents.push({
        id: `option-irrelevant`,
        text: '#Irrelevant',
        category: 'System actions'
      });

      allTopics = allTopics.concat(dialogIntents);
    }

    if (!_.isEmpty(topics)) {
      allTopics.push({
        id: `fallback`,
        text: 'Fallback',
        category: 'System actions'
      });
      allTopics.push({
        id: `welcome`,
        text: 'Greet customer',
        category: 'System actions'
      });
    }

    return allTopics;
  }
);

export const selectSelectedTopics = createSelector(
  [
    (state: RootState) => state.analytics?.filters.topics,
    (state: RootState) => state.analytics?.filters.noIntents,
    selectAllTopics
  ],
  (topics, isIrrelevant, allTopics) => {
    const topicsSelected = [];
    if (!allTopics && !isIrrelevant) {
      return;
    }
    if (!_.isEmpty(topics)) {
      _.forEach(topics, topic => {
        _.find(allTopics, item => {
          if (item.id === topic || item.text === '#' + topic) {
            topicsSelected.push(item);
          }
        });
      });
    }
    //if irrelevant is selected
    if (isIrrelevant) {
      topicsSelected.push({
        id: `option-irrelevant`,
        text: '#Irrelevant',
        category: 'System actions'
      });
    }
    return topicsSelected;
  }
);

export const selectAllEntities = (state: RootState) => {
  const allEntities = [];
  _.forEach(state?.models?.entities, entity => {
    allEntities.push({
      id: entity.entity as string,
      text: `@${entity.entity}` as string
    });
  });
  return allEntities;
};

export const selectSelectedEntities = createSelector(
  [(state: RootState) => state.analytics?.filters?.entities, selectAllEntities],
  (entities, allEntities) => {
    if (!allEntities) {
      return;
    }
    const entitiesSelected = [];
    if (!_.isEmpty(entities)) {
      _.forEach(entities, entity => {
        _.find(allEntities, item => {
          if (item.id === entity) {
            entitiesSelected.push(item);
          }
        });
      });
    }
    return entitiesSelected;
  }
);

export const selectAllRecognitionTypes = (state: RootState) => {
  const isAIGuidedActionEnabled = isFeatureEnabled(
    state,
    FeatureFlag.ACTIONS_NEW_IA_AI_GUIDED
  );
  const isGeneralPurposeAnsweringEnabled = isFeatureEnabled(
    state,
    FeatureFlag.ACTIONS_GENERATIVE_AI_BASE_LLM
  );
  const isConvoSearchEnabled = selectIsConversationalSearchEnabled(state);
  let recognitionTypes = [
    {
      id: 'recognition-option-1',
      text: messages.unrecognized.defaultMessage,
      queryText: AnalyticsActionSkillTypes.UNRECOGNIZED
    },
    {
      id: 'recognition-option-2',
      text: messages.recognized.defaultMessage,
      queryText: AnalyticsActionSkillTypes.ALL_RECOGNIZED
    },
    {
      id: 'recognition-option-3',
      text: messages.customBuiltActions.defaultMessage,
      queryText: AnalyticsActionSkillTypes.CUSTOM
    },
    {
      id: 'recognition-option-4',
      text: messages.skillBasedActions.defaultMessage,
      queryText: AnalyticsActionSkillTypes.SKILL
    }
  ];
  if (isAIGuidedActionEnabled) {
    recognitionTypes.push({
      id: 'recognition-option-5',
      text: messages.aiGuidedActions.defaultMessage,
      queryText: AnalyticsActionSkillTypes.AI_GUIDED
    });
  }
  if (isGeneralPurposeAnsweringEnabled) {
    recognitionTypes.push({
      id: 'recognition-option-6',
      text: messages.generalPurposeAnswering.defaultMessage,
      queryText: AnalyticsActionSkillTypes.GENERAL_PURPOSE_ANSWERING
    });
  }
  if (isConvoSearchEnabled) {
    recognitionTypes.push({
      id: 'recognition-option-7',
      text: messages.conversationalSearch.defaultMessage,
      queryText: AnalyticsActionSkillTypes.CONVO_SEARCH
    });
  }
  recognitionTypes = recognitionTypes.concat([
    {
      id: 'recognition-option-8',
      text: messages.search.defaultMessage,
      queryText: AnalyticsActionSkillTypes.SEARCH
    },
    {
      id: 'recognition-option-9',
      text: messages.zeroResults.defaultMessage,
      queryText: AnalyticsActionSkillTypes.SEARCH_ZERO_RESULTS_ONLY
    },
    {
      id: 'recognition-option-10',
      text: messages.systemActions.defaultMessage,
      queryText: AnalyticsActionSkillTypes.SYSTEM_ACTIONS
    },
    {
      id: 'recognition-option-11',
      text: messages.intents.defaultMessage,
      queryText: AnalyticsActionSkillTypes.INTENTS
    }
  ]);
  return recognitionTypes;
};

export const selectAllResolutionMethods = (state: RootState) => {
  const allResolutionMethods = [];
  _.forEach(state?.models?.resolution_method, method => {
    allResolutionMethods.push({
      id: method as string,
      text: `@${method}` as string
    });
  });
  return allResolutionMethods;
};

export const selectSelectedResolutionMethod = createSelector(
  [
    (state: RootState) => state.analytics?.filters?.resolution_method,
    selectAllRecognitionTypes
  ],
  (resolution_method, allResolutionMethods) => {
    if (!allResolutionMethods) {
      return;
    }
    const resolutionMethodsSelected = [];
    if (!_.isEmpty(resolution_method)) {
      _.forEach(resolution_method, method => {
        _.find(allResolutionMethods, item => {
          if (item.queryText === method) {
            resolutionMethodsSelected.push(item);
          }
        });
      });
    }
    return resolutionMethodsSelected;
  }
);

export const selectLogs = (state: RootState, allLogs) => {
  const { requests, pagination } = allLogs
    ? state.analytics?.allLogs
    : state.analytics?.messageLogs;
  const { currentPageSize, currentPage } = pagination;
  const start = currentPageSize * (currentPage - 1);
  const end = currentPageSize * currentPage;
  return requests.slice(start, end);
};

export const selectConvoSearchLogs = (state: RootState) => {
  const { requests, pagination } = state.analytics?.convoSearchMessageLogs;
  const { currentPageSize, currentPage } = pagination;
  const start = currentPageSize * (currentPage - 1);
  const end = currentPageSize * currentPage;
  return requests.slice(start, end);
};

export const areFrequentActionsLoading = (state: RootState): boolean => {
  const { loading } = state.analytics?.frequentActions;
  return (
    loading?.includes(ApiStatus.IDLE) || loading?.includes(ApiStatus.LOADING)
  );
};

export const selectUnrecognizedRequests = (state: RootState) => {
  const { requests } = state.analytics?.messageLogs;
  const unrecognizedRequests = [];
  //only show the first request of each conversation
  _.map(requests, log => {
    if (log.conversations?.start && unrecognizedRequests.length <= 10) {
      unrecognizedRequests.push(log.requests.message);
    }
  });
  return unrecognizedRequests;
};

export const selectActionCompletion = (state: RootState) => {
  return state.analytics?.actionCompletion?.actions;
};

export const dialogHasDataSelector = (state: RootState): boolean => {
  return state.analytics?.skillHasData?.hasCoexistenceData;
};

export const selectLeastCompletedActions = (state: RootState, intl) => {
  const { actions } = state.analytics?.actionCompletion;
  const data = [];

  if (!_.isEmpty(actions)) {
    data.push({
      heading: true,
      firstHeading: intl.formatMessage(messages.actions),
      secondHeading: intl.formatMessage(messages.completion)
    });
  }

  actions?.forEach(action => {
    data.push({
      title: action.actions.title,
      actionID: action.actions.id,
      value: action.completion + '%'
    });
  });

  return data;
};

export const areActionCompletionLoading = (state: RootState): boolean => {
  const { loading } = state.analytics?.actionCompletion;
  return (
    loading?.includes(ApiStatus.IDLE) || loading?.includes(ApiStatus.LOADING)
  );
};

export const selectPercentageData = (state: RootState) => {
  const { requests, pagination } = state.analytics?.singleActionPercentage;
  const { currentPageSize, currentPage } = pagination;
  const start = currentPageSize * (currentPage - 1);
  const end = currentPageSize * currentPage;
  return requests.slice(start, end);
};

export const selectIncompleteData = (state: RootState) => {
  const { requests, pagination } = state.analytics?.singleActionIncomplete;
  const { currentPageSize, currentPage } = pagination;
  const start = currentPageSize * (currentPage - 1);
  const end = currentPageSize * currentPage;
  return requests.slice(start, end);
};

export const selectCompleteData = (state: RootState) => {
  const { requests, pagination } = state.analytics?.singleActionComplete;
  const { currentPageSize, currentPage } = pagination;
  const start = currentPageSize * (currentPage - 1);
  const end = currentPageSize * currentPage;
  return requests.slice(start, end);
};

export const arePercentageDataLoading = (state: RootState): boolean => {
  const { loading } = state.analytics?.singleActionPercentage;
  return (
    loading?.includes(ApiStatus.IDLE) || loading?.includes(ApiStatus.LOADING)
  );
};

export const areIncompleteDataLoading = (state: RootState): boolean => {
  const { loading } = state.analytics?.singleActionIncomplete;
  return (
    loading?.includes(ApiStatus.IDLE) || loading?.includes(ApiStatus.LOADING)
  );
};

export const areCompleteDataLoading = (state: RootState): boolean => {
  const { loading } = state.analytics?.singleActionComplete;
  return (
    loading?.includes(ApiStatus.IDLE) || loading?.includes(ApiStatus.LOADING)
  );
};

export const areRecognitionHistogramLoading = (state: RootState): boolean => {
  const { loading } = state.analytics?.recognition;
  return (
    loading?.includes(ApiStatus.IDLE) || loading?.includes(ApiStatus.LOADING)
  );
};

export const areActionCompletionHistogramLoading = (
  state: RootState
): boolean => {
  const { loading } = state.analytics?.actionCompletionHistogram;
  return (
    loading?.includes(ApiStatus.IDLE) || loading?.includes(ApiStatus.LOADING)
  );
};

export const selectAllReasons = (state: RootState, type: string) => {
  const incompleteReasons = [
    {
      id: 'incomplete-option1',
      text: 'Incomplete: Escalated to agent'
    },
    {
      id: 'incomplete-option2',
      text: 'Incomplete: Started new action'
    },
    {
      id: 'incomplete-option3',
      text: 'Incomplete: Stuck on step'
    },
    {
      id: 'incomplete-option4',
      text: 'Incomplete: Abandoned'
    },
    {
      id: 'incomplete-option5',
      text: 'Incomplete: Trigger phrase'
    }
  ];
  const completeReasons = [
    {
      id: 'complete-option1',
      text: 'Complete: Assistant'
    },
    {
      id: 'complete-option2',
      text: 'Complete: Agent handoff'
    }
  ];
  if (type === 'incomplete') {
    return incompleteReasons;
  } else if (type === 'complete') {
    return completeReasons;
  } else {
    return completeReasons.concat(incompleteReasons);
  }
};

export const selectSelectedReasons = (state: RootState, type: string) => {
  const allResponses = selectAllReasons(state, type);
  const reasons =
    type === 'incomplete'
      ? state.analytics?.filters.incompleteReasons
      : type === 'complete'
      ? state.analytics?.filters.completeReasons
      : state.analytics?.filters.percentageReasons;
  const reasonsSelected = [];
  _.map(reasons, reason => {
    _.find(allResponses, item => {
      if (item.text === reason) {
        reasonsSelected.push(item);
      }
    });
  });
  return reasonsSelected;
};

export const areFiltersApplied = (state: RootState): boolean => {
  const { filters } = state.analytics;
  let filtersApplied = false;

  if (
    !_.isEmpty(filters.search) ||
    !_.isEmpty(filters.containment) ||
    !_.isEmpty(filters.recognition) ||
    !_.isEmpty(filters.topics) ||
    !_.isEmpty(filters.keywords) ||
    !_.isEmpty(filters.incompleteReasons) ||
    !_.isEmpty(filters.completeReasons) ||
    !_.isEmpty(filters.percentageReasons) ||
    !_.isEmpty(filters?.entities) ||
    !_.isEmpty(filters?.convoSearchResponseType) ||
    !_.isEmpty(filters?.resolution_method) ||
    filters.noIntents === true
  ) {
    filtersApplied = true;
  }

  return filtersApplied;
};

export const areScoresValid = (state: RootState): boolean => {
  const { convoSearchMessageLogs } = state.analytics;

  return (
    isValidNumber(convoSearchMessageLogs?.responseConfidence) &&
    isValidNumber(convoSearchMessageLogs?.retrievalConfidence) &&
    isValidNumber(convoSearchMessageLogs?.extractiveness) &&
    isValidNumber(convoSearchMessageLogs?.citation) &&
    isValidNumber(convoSearchMessageLogs?.responseLength)
  );
};

export const liveNotPublished = (state: RootState): boolean => {
  const { filters } = state.analytics;

  const liveAssistantId = selectTargetEnvironmentId(state);
  const liveEnvironmentSelected = filters.environment === liveAssistantId;
  const isPublishLoading = selectIsPublishStatusLoading(state);
  const actionsLoading = areActionsLoading(state);
  const actionPublishLoading = actionsLoading || isPublishLoading;
  const hasPublished = hasPublishedVersion(state);

  return liveEnvironmentSelected && !actionPublishLoading && !hasPublished;
};

export const livePublishedOrAnotherEnvironment = (
  state: RootState
): boolean => {
  const { filters } = state.analytics;

  const liveAssistantId = selectTargetEnvironmentId(state);
  const liveEnvironmentSelected = filters.environment === liveAssistantId;
  const hasPublished = hasPublishedVersion(state);

  return (liveEnvironmentSelected && hasPublished) || !liveEnvironmentSelected;
};

export const selectRecognitionClusters = (state: RootState) => {
  return state?.analytics?.recognitionClusters;
};

export const selectRecognitionClustersLoading = (state: RootState): boolean => {
  const clusters = state?.analytics?.recognitionClusters;
  let isLoading = false;
  if (
    (clusters.loading === ApiStatus.LOADING &&
      clusters.status !== unrecognizedPollingStatus.UPDATING) ||
    (clusters.status === unrecognizedPollingStatus.IN_PROGRESS &&
      clusters.groups.length === 0)
  ) {
    isLoading = true;
  }
  return isLoading;
};

export const ninetyDayLogs = (state: RootState, plan: PlanName): boolean =>
  plan === PlanName.PREMIUM ||
  plan === PlanName.ENTERPRISE_DATA_ISOLATION ||
  isCpd;

export const getConvoSearchResponseType = (state: RootState): string =>
  state?.analytics.filters.convoSearchResponseType;
